import { ceilingResolutionValues } from 'src/domains/calculators/utilities/ceiling';
import { roundResolutionValues } from 'src/domains/calculators/utilities/round';
import { Resolution } from '../../../utilities/types';
import {
  FramingChartVisualElementResolutions,
  TriangleResolution,
} from '../FramingChart/types';
import { NETFLIX_LOGO_DIMENSIONS } from '../../constants';

export const CHART_CONSTANTS = {
  CENTER_CROSS: {
    LINE_LENGTH_RATIO: 0.07,
    SPACING_RATIO: 0.2,
  },
  LOGO: {
    HEIGHT_RATIO: 0.3,
  },
  TEST_PATTERN: {
    SIZE_RATIO: 0.15,
    PADDING_RATIO: 0.05,
    POSITIONS: {
      LEFT_EDGE: 1 / 9,
      RIGHT_EDGE: 8 / 9,
    },
  },
  TRIANGLE_SIZE_RATIO: 0.04,
  TEXT_POSITION_RATIO: 0.65,
};

export function getRoundedResolution(
  resolution: Resolution
): Resolution {
  const roundedValues = ceilingResolutionValues(
    roundResolutionValues(resolution),
    2
  );

  return {
    width: roundedValues.width,
    height: roundedValues.height,
  };
}

export function getTriangleResolution({
  aspectRatioResolution,
  squeeze,
}: {
  aspectRatioResolution: Resolution;
  squeeze: number;
}): TriangleResolution {
  const { height: aspectRatioHeight } = aspectRatioResolution;

  // Base triangle height (same for both orientations)
  const triangleHeight =
    aspectRatioHeight * CHART_CONSTANTS.TRIANGLE_SIZE_RATIO;

  // For vertical triangles (pointing up or down):
  // - width = horizontal half-base, gets squeezed horizontally
  // - height = vertical height (triangle height)
  const verticalWidth = triangleHeight / Math.sqrt(3) / squeeze;
  const vertical: Resolution = {
    width: verticalWidth,
    height: triangleHeight,
  };

  // For horizontal triangles (pointing left or right):
  // - width = horizontal extent (base-to-tip distance), gets squeezed horizontally
  // - height = vertical half-base
  const horizontalWidth = triangleHeight / squeeze;
  const horizontalHeight = triangleHeight / Math.sqrt(3);
  const horizontal: Resolution = {
    width: horizontalWidth,
    height: horizontalHeight,
  };

  return { vertical, horizontal };
}

export function getTestPatternResolution({
  framingDecision,
  squeeze,
}: {
  framingDecision: Resolution;
  squeeze: number;
}): Resolution {
  const { TEST_PATTERN } = CHART_CONSTANTS;

  const starHeight = framingDecision.height * TEST_PATTERN.SIZE_RATIO;
  const starWidth = starHeight / squeeze;

  return { width: starWidth, height: starHeight };
}

export function getCrossResolution({
  containerResolution,
  squeeze,
}: {
  containerResolution: Resolution;
  squeeze: number;
}): Resolution {
  const crossHeight =
    containerResolution.height *
    CHART_CONSTANTS.CENTER_CROSS.LINE_LENGTH_RATIO;

  return {
    height: crossHeight,
    width: crossHeight / squeeze,
  };
}

export function getLogoResolution({
  containerResolution,
  squeeze,
  logoResolution,
}: {
  containerResolution: Resolution;
  squeeze: number;
  logoResolution: Resolution;
}): Resolution {
  const logoAspectRatio =
    logoResolution.width / logoResolution.height;

  const height =
    containerResolution.height * CHART_CONSTANTS.LOGO.HEIGHT_RATIO;
  const width = (height * logoAspectRatio) / squeeze;

  return {
    width,
    height,
  };
}

interface VisualElementCalculationInputs {
  framingDecision: Resolution;
  framingDecisionProtection: Resolution;
  squeeze: number;
  desqueezed?: boolean;
}

/**
 * Calculate all visual element resolutions for the framing chart
 */
export function getFramingChartElementResolutions(
  inputs: VisualElementCalculationInputs
): FramingChartVisualElementResolutions {
  const {
    framingDecision,
    framingDecisionProtection,
    squeeze,
    desqueezed,
  } = inputs;

  const effectiveSqueeze = desqueezed ? 1 : squeeze;

  return {
    textScale: (() => {
      return 1 / effectiveSqueeze;
    })(),
    testPatternResolution: getTestPatternResolution({
      framingDecision,
      squeeze: effectiveSqueeze,
    }),
    centerCrossResolution: getCrossResolution({
      containerResolution: framingDecision,
      squeeze: effectiveSqueeze,
    }),
    logoResolution: getLogoResolution({
      containerResolution: framingDecision,
      squeeze: effectiveSqueeze,
      logoResolution: NETFLIX_LOGO_DIMENSIONS,
    }),
    triangleResolution: getTriangleResolution({
      aspectRatioResolution: framingDecisionProtection,
      squeeze: effectiveSqueeze,
    }),
  };
}
